import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", info: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ status: "paid", info: data }); return; }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
        if (attempts >= 8) { setState({ status: "timeout" }); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 8) setState({ status: "error" });
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="px-6 py-28 text-center max-w-lg mx-auto" data-testid="billing-success-page">
      {state.status === "checking" && (<><Loader2 size={40} className="animate-spin mx-auto text-rose-400" /><h1 className="font-display font-bold text-2xl mt-5">Confirming your payment…</h1><p className="text-zinc-400 mt-2 text-sm">This only takes a few seconds.</p></>)}
      {state.status === "paid" && (
        <>
          <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
          <h1 className="font-display font-extrabold text-3xl mt-5">You're all set! 🎉</h1>
          <p className="text-zinc-300 mt-3">{state.info?.plan_name}{state.info?.entity_name ? ` is now active for ${state.info.entity_name}` : " is now active"}.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/dashboard" className="bg-rose-500 hover:bg-rose-600 font-bold px-6 py-2.5 rounded-full text-sm" data-testid="billing-goto-dashboard">Go to dashboard</Link>
            <Link to="/pricing" className="border border-white/15 px-6 py-2.5 rounded-full text-sm">View plans</Link>
          </div>
        </>
      )}
      {["expired", "timeout", "error"].includes(state.status) && (
        <>
          <XCircle size={44} className="mx-auto text-amber-400" />
          <h1 className="font-display font-bold text-2xl mt-5">{state.status === "expired" ? "Checkout session expired" : "We couldn't confirm the payment yet"}</h1>
          <p className="text-zinc-400 mt-2 text-sm">{state.status === "expired" ? "No charge was made. You can try again anytime." : "If you completed payment, it may still process — check your dashboard billing in a minute."}</p>
          <Link to="/pricing" className="inline-block mt-6 border border-white/15 px-6 py-2.5 rounded-full text-sm">Back to pricing</Link>
        </>
      )}
    </div>
  );
}
